/**
 * Guarded localStorage wrapper for persistent progress. Any failure (no
 * localStorage, blocked, parse error) degrades gracefully to empty progress.
 */
import { emptyProgress, normalizeProgress, type Progress } from './progress.ts';

const KEY = 'beyblade.progress.v1';

export function loadProgress(): Progress {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return emptyProgress();
    return normalizeProgress(JSON.parse(raw));
  } catch {
    return emptyProgress();
  }
}

export function saveProgress(p: Progress): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* ignore — persistence is best-effort */
  }
}

const MUTE_KEY = 'beyblade.muted.v1';

export function loadMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function saveMuted(muted: boolean): void {
  try {
    localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
  } catch {
    /* ignore */
  }
}

const PREFS_KEY = 'beyblade.prefs.v1';

export interface Prefs {
  arena: string;
  difficultyIdx: number;
}

export function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    const p = raw ? JSON.parse(raw) : {};
    return {
      arena: typeof p.arena === 'string' ? p.arena : 'lab',
      difficultyIdx: Number.isInteger(p.difficultyIdx) ? p.difficultyIdx : 1,
    };
  } catch {
    return { arena: 'lab', difficultyIdx: 1 };
  }
}

export function savePrefs(p: Prefs): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    /* ignore */
  }
}
